import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiBlob, apiFetch } from "@/api/client";

/*
 * /api/me/email-drafts — client emails my actions created (sending an invoice, closing a ticket), waiting for me
 * to review them in the email form and press Send. Nothing goes to the client until then.
 */

export interface EmailDraftSummary {
  id: string;
  entity: string | null;
  entity_id: string | null;
  to: string[];
  subject: string;
  preview: string;
  created_at: string | null;
}

export interface EmailAttachment {
  id: string;
  file_name: string;
  content_type: string | null;
  size: number;
}

export interface EmailDraft {
  id: string;
  status: string;
  entity: string | null;
  entity_id: string | null;
  from: string | null;
  to: string[];
  cc: string[];
  subject: string;
  body_html: string;
  attachments: EmailAttachment[];
  max_attachment_bytes: number;
  created_at: string | null;
}

export interface SendDraftBody {
  to: string[];
  cc: string[];
  subject: string;
  body_html: string;
}

const BASE = "/me/email-drafts";
export const draftKeys = {
  all: ["email-drafts"] as const,
  one: (id: string) => ["email-drafts", id] as const,
};

export function useMyEmailDrafts(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: draftKeys.all,
    queryFn: ({ signal }) => apiFetch<EmailDraftSummary[]>(BASE, { signal }),
    enabled: options.enabled ?? true,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useEmailDraft(id: string | null) {
  return useQuery({
    queryKey: draftKeys.one(id ?? ""),
    queryFn: ({ signal }) => apiFetch<EmailDraft>(`${BASE}/${encodeURIComponent(id ?? "")}`, { signal }),
    enabled: !!id,
    staleTime: Infinity, // the form owns the edits; only attachment changes replace it
  });
}

function useDone() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: draftKeys.all });
    // Lists showing email history (an invoice's emails) pick up the new status.
    void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "email-drafts" && q.getObserversCount() > 0 });
  };
}

export function useSendDraft(id: string) {
  const done = useDone();
  return useMutation({
    mutationFn: (body: SendDraftBody) =>
      apiFetch<{ id: string; status: string; to: string[] }>(`${BASE}/${encodeURIComponent(id)}/send`, { method: "POST", body }),
    onSuccess: done,
  });
}

export function useDiscardDraft(id: string) {
  const done = useDone();
  return useMutation({
    mutationFn: () => apiFetch(`${BASE}/${encodeURIComponent(id)}/discard`, { method: "POST" }),
    onSuccess: done,
  });
}

export function useAttachmentChange(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (change: { add: File } | { remove: string }) => {
      const path = `${BASE}/${encodeURIComponent(id)}/attachments`;
      if ("add" in change) {
        const form = new FormData();
        form.append("file", change.add, change.add.name);
        return apiFetch<EmailDraft>(path, { method: "POST", rawBody: form });
      }
      return apiFetch<EmailDraft>(`${path}/${encodeURIComponent(change.remove)}`, { method: "DELETE" });
    },
    onSuccess: (draft) => {
      // Keep the form's edits: only the attachment list comes from the server.
      qc.setQueryData<EmailDraft>(draftKeys.one(id), (old) => (old ? { ...old, attachments: draft.attachments } : draft));
    },
  });
}

export async function openAttachment(draftId: string, fileId: string): Promise<void> {
  const tab = window.open("", "_blank");
  const url = URL.createObjectURL(await apiBlob(`${BASE}/${encodeURIComponent(draftId)}/attachments/${encodeURIComponent(fileId)}`));
  if (tab) tab.location.href = url;
  else window.location.assign(url);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** "Invoice", "Ticket": what a draft is about, for headings. */
export function draftSubjectOf(entity: string | null): string {
  if (entity === "invoice") return "Invoice";
  if (entity === "ticket") return "Ticket";
  return "Record";
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

const EMAIL = /^[^@\s;,<>"]+@[^@\s;,<>"]+\.[^@\s;,<>"]+$/;
export const isEmail = (v: string) => EMAIL.test(v.trim());
